/* ------------------------------------------------------------------ */
/* Centralized metric explanations for TokenIntel + IntelDemo         */
/* Legal-safe: factual language only. No advisory verbs.              */
/* ------------------------------------------------------------------ */

export interface MetricExplain {
  title: string;
  what: string;
  why: string;
  detection: string;
  falsePositives: string;
  confidenceNote: string;
}

export const METRIC_EXPLANATIONS: Record<string, MetricExplain> = {
  sellabilityCheck: {
    title: 'Sellability Check',
    what: 'Evaluates whether a token contract restricts selling after purchase through hidden code mechanisms.',
    why: 'Tokens with sell restrictions can trap funds permanently. This is one of the most common on-chain fraud patterns.',
    detection: 'Bytecode analysis of transfer functions, buy/sell tax simulation, and comparison against known restrictive patterns.',
    falsePositives: 'Some legitimate tokens implement temporary transfer restrictions during launch or migration periods.',
    confidenceNote: 'Higher confidence indicates more data sources confirmed the analysis. Lower confidence means fewer sources were available.',
  },
  buyTax: {
    title: 'Buy Tax',
    what: 'The percentage fee deducted when purchasing this token on a DEX.',
    why: 'High buy taxes reduce the effective value received per trade and can indicate extractive tokenomics.',
    detection: 'Simulated purchase transactions on-chain to measure actual token received versus expected.',
    falsePositives: 'Some reflection/reward tokens intentionally redistribute fees to holders.',
    confidenceNote: 'Based on simulated swap results. Higher confidence means more simulations confirmed the rate.',
  },
  sellTax: {
    title: 'Sell Tax',
    what: 'The percentage fee deducted when selling this token on a DEX.',
    why: 'Abnormally high sell taxes (above 10%) can prevent profitable exits. Extremely high values (90%+) indicate likely sell traps.',
    detection: 'Simulated sell transactions measuring actual output versus expected.',
    falsePositives: 'Some tokens have dynamic tax rates that decrease over time or based on holding period.',
    confidenceNote: 'Based on simulated swap results. Tax rates can change if the contract owner modifies parameters.',
  },
  mintFunction: {
    title: 'Mint Function',
    what: 'Whether the contract contains a function allowing creation of new tokens beyond the initial supply.',
    why: 'Unrestricted minting can dilute holder value. However, many legitimate tokens use controlled minting for rewards.',
    detection: 'Static analysis of contract bytecode for mint-related function signatures.',
    falsePositives: 'Standard token patterns like ERC-20 with capped mint or governance-controlled mint are common and legitimate.',
    confidenceNote: 'Binary detection (present/absent). Does not indicate whether the function is restricted or unrestricted.',
  },
  blacklist: {
    title: 'Blacklist Function',
    what: 'Whether the contract can block specific addresses from transferring tokens.',
    why: 'Blacklist functions can be used to freeze holder funds. They are also used legitimately for compliance.',
    detection: 'Static analysis of contract bytecode for blacklist/blocklist function signatures.',
    falsePositives: 'Many compliant stablecoins and regulated tokens include blacklist functions for legal requirements.',
    confidenceNote: 'Binary detection. The presence of a blacklist function does not indicate misuse.',
  },
  pauseFunction: {
    title: 'Pause Function',
    what: 'Whether the contract can halt all transfers globally.',
    why: 'A paused contract prevents all trading activity. This can be used for emergencies or to trap liquidity.',
    detection: 'Static analysis of contract bytecode for pause/unpause function signatures.',
    falsePositives: 'Emergency pause is a standard security pattern in audited DeFi protocols.',
    confidenceNote: 'Binary detection. Many well-audited contracts include pause functionality as a safety mechanism.',
  },
  ownershipRenounced: {
    title: 'Ownership Status',
    what: 'Whether the contract deployer has permanently given up admin control.',
    why: 'Renounced ownership means no single party can modify contract parameters, reducing rug-pull risk.',
    detection: 'On-chain verification of owner address (typically set to 0x0 or dead address).',
    falsePositives: 'Some projects use multi-sig governance instead of renouncing, which is also a valid security model.',
    confidenceNote: 'High confidence when verified on-chain. Proxy contracts may have hidden ownership layers.',
  },
  liquidity: {
    title: 'Liquidity Profile',
    what: 'The total value of assets available for trading in decentralized exchange pools.',
    why: 'Low liquidity means high slippage and difficulty exiting positions. Sudden liquidity removal (rug pull) is a common attack.',
    detection: 'Aggregated DEX pool data across major platforms (Uniswap, SushiSwap, PancakeSwap, etc.).',
    falsePositives: 'New tokens naturally start with lower liquidity. Liquidity can fluctuate with market conditions.',
    confidenceNote: 'Based on real-time pool data. Confidence decreases if only one pool is detected.',
  },
  lpLock: {
    title: 'LP Lock Status',
    what: 'Whether liquidity provider tokens are locked in a time-locked contract, preventing removal.',
    why: 'Locked LP prevents the deployer from pulling liquidity. Longer locks provide stronger protection against rug pulls.',
    detection: 'Verification against known lock platforms (Unicrypt, Team Finance, PinkSale, etc.).',
    falsePositives: 'Some legitimate projects use alternative liquidity management strategies without traditional LP locks.',
    confidenceNote: 'High confidence when verified through a recognized lock platform. Self-reported locks have lower confidence.',
  },
  holders: {
    title: 'Holder Distribution',
    what: 'Analysis of how token supply is distributed across wallet addresses.',
    why: 'High concentration (few wallets holding large percentages) increases the risk of coordinated selling pressure.',
    detection: 'On-chain analysis of token balances across all holder addresses.',
    falsePositives: 'Exchange hot wallets and bridge contracts often hold large amounts without posing concentration risk.',
    confidenceNote: 'Based on current on-chain data. Holder counts can change rapidly during market events.',
  },
  topHolders: {
    title: 'Top Holder Concentration',
    what: 'The combined percentage of token supply held by the top 10 and top 20 wallets.',
    why: 'If a small group controls a large share of supply, their selling activity can significantly impact price.',
    detection: 'Aggregated balance analysis of the largest token holders, excluding known exchange and contract addresses.',
    falsePositives: 'Dead/burn addresses, exchange wallets, and protocol treasury wallets inflate concentration metrics.',
    confidenceNote: 'Excludes known contract addresses where possible. Manual override addresses may not be fully filtered.',
  },
  smartMoney: {
    title: 'Smart Money Signals',
    what: 'Tracks the buying and selling activity of wallets historically associated with early successful trades.',
    why: 'Patterns from historically profitable wallets can indicate informed activity, though past performance is not predictive.',
    detection: 'Proprietary wallet scoring based on historical trade outcomes, entry timing, and portfolio performance.',
    falsePositives: 'Smart money wallets can be wrong. Wash trading can simulate smart money activity.',
    confidenceNote: 'Signal strength depends on the number of tracked wallets active in this token. Small samples reduce reliability.',
  },
  network: {
    title: 'Network Analysis',
    what: 'Maps the flow of funds between this token and connected entities (exchanges, other contracts, wallets).',
    why: 'Unusual fund flows — such as circular transfers or connections to flagged addresses — can indicate manipulation.',
    detection: 'Graph analysis of on-chain transactions tracing fund origins and destinations.',
    falsePositives: 'High-traffic tokens naturally have complex networks. Not all connections indicate malicious activity.',
    confidenceNote: 'Depends on the depth of analysis. Deeper traces increase confidence but also computation time.',
  },
  exploits: {
    title: 'Known Exploit Check',
    what: 'Whether this contract address or its deployer has been linked to known security incidents.',
    why: 'Addresses linked to previous exploits, hacks, or scam reports carry elevated risk regardless of current behavior.',
    detection: 'Cross-reference against threat intelligence databases, security advisories, and community reports.',
    falsePositives: 'Shared deployer addresses (e.g., factory contracts) may be incorrectly flagged due to other deployments.',
    confidenceNote: 'Higher confidence when multiple independent sources confirm the association.',
  },
  approvals: {
    title: 'Token Approvals',
    what: 'Whether the contract requires or requests unlimited token spending approvals from users.',
    why: 'Unlimited approvals allow a contract to move any amount of tokens from a wallet, even after the initial transaction.',
    detection: 'Analysis of approval requests in transaction simulation and contract interaction patterns.',
    falsePositives: 'Many legitimate DeFi protocols request unlimited approvals for gas efficiency. This is standard practice.',
    confidenceNote: 'Detection is based on simulated or observed approval calls. Actual risk depends on the contract behavior.',
  },
  domains: {
    title: 'Domain & Social Presence',
    what: 'Analysis of the project website age, social media presence, and community engagement metrics.',
    why: 'Established web presence and active communities correlate with longer-running projects, though this does not eliminate risk.',
    detection: 'WHOIS data, social media API metrics, and community platform member counts.',
    falsePositives: 'New legitimate projects may have young domains. Viral tokens can have large followings quickly.',
    confidenceNote: 'Social metrics can be manipulated through bot activity. Domain age is more reliable but not conclusive.',
  },
  threatProfile: {
    title: 'Threat Intelligence Profile',
    what: 'Aggregated threat assessment from multiple security intelligence sources.',
    why: 'Cross-referencing multiple independent threat databases provides a more comprehensive risk picture than any single source.',
    detection: 'Real-time queries against CryptoGuardians threat database, which aggregates data from multiple security providers.',
    falsePositives: 'Stale data in threat databases can flag addresses that have been resolved or are no longer active threats.',
    confidenceNote: 'Confidence increases with the number of sources that agree. Conflicting sources reduce the overall confidence.',
  },
  walletRisk: {
    title: 'Wallet Risk Assessment',
    what: 'Analysis of the deployer wallet and associated wallets for historical risk indicators.',
    why: 'Deployer wallets linked to previous failed or fraudulent projects carry forward risk to new deployments.',
    detection: 'Historical analysis of deployer wallet activity, associated deployments, and fund flow patterns.',
    falsePositives: 'Prolific deployers (e.g., token factories, launchpad contracts) may have mixed histories.',
    confidenceNote: 'Based on available on-chain history. Newer wallets with limited history have lower confidence scores.',
  },
  riskScore: {
    title: 'Overall Risk Score',
    what: 'A normalized 0-10 score aggregating all individual risk factors into a single comparable metric.',
    why: 'Provides a quick summary of the overall risk profile. Individual metric breakdowns provide the detailed picture.',
    detection: 'Weighted combination of all individual metrics, calibrated against known outcomes.',
    falsePositives: 'Aggregate scores can be skewed by one dominant factor. Always review the individual breakdowns.',
    confidenceNote: 'Overall confidence reflects the lowest individual metric confidence, ensuring conservative assessment.',
  },
  confidence: {
    title: 'Confidence Level',
    what: 'How certain the analysis is, based on data availability and source agreement.',
    why: 'Low-confidence results mean limited data was available. High confidence means multiple sources agreed.',
    detection: 'Calculated from the number of responsive data sources, data freshness, and inter-source agreement.',
    falsePositives: 'Not applicable — confidence is a meta-metric about the analysis itself.',
    confidenceNote: 'High (>80%): strong data support. Medium (50-80%): partial data. Low (<50%): limited sources available.',
  },
  fundingAttribution: {
    title: 'Deployer Funding Source',
    what: 'Traces where the token deployer received their initial funding to deploy the contract.',
    why: 'Funding from mixers or unknown private wallets can indicate an attempt to hide identity, which correlates with higher scam probability.',
    detection: 'On-chain analysis of the first inbound transaction to the deployer wallet, matched against known exchange, mixer, and bridge addresses.',
    falsePositives: 'Privacy-conscious legitimate developers may use bridges or privacy tools for legitimate reasons.',
    confidenceNote: 'Higher confidence when the funding source matches a known labeled address.',
  },
  lockerIntel: {
    title: 'Liquidity Lock Analysis',
    what: 'Determines whether LP tokens are locked in a recognized time-lock contract and for how long.',
    why: 'Locked liquidity prevents the deployer from suddenly withdrawing trading liquidity (rug pull). Longer locks and trusted platforms provide stronger protection.',
    detection: 'Analysis of LP holder data to identify tokens held by known lock platform contracts.',
    falsePositives: 'Some legitimate projects use alternative liquidity management or protocol-owned liquidity without traditional locks.',
    confidenceNote: 'High confidence when LP is held by a recognized lock contract.',
  },
  socialPresence: {
    title: 'Social & Web Presence',
    what: 'Assessment of the project website, social media accounts (Twitter/X, Telegram), and developer activity (GitHub).',
    why: 'Projects with no website, dead social accounts, or no development activity are more likely to be abandoned or fraudulent.',
    detection: 'Verification of website availability and social media link existence from DEX listing data.',
    falsePositives: 'Very new legitimate projects may not yet have established social presence.',
    confidenceNote: 'Based on data available from DEX listings and website health checks.',
  },
  contractAge: {
    title: 'Contract Age',
    what: 'Days since the token contract was created on-chain.',
    why: 'Very new contracts have less track record and may carry higher uncertainty.',
    detection: 'Derived from the contract creation transaction timestamp on the block explorer.',
    falsePositives: 'Some established projects deploy new contract versions while maintaining continuity.',
    confidenceNote: 'Based on block explorer data. Highly reliable when available.',
  },
  pairAge: {
    title: 'Top Liquidity Pair Age',
    what: 'Days since the main trading pair was created on a DEX (may be newer than the token contract).',
    why: 'Very new trading pairs may have less established liquidity and price stability.',
    detection: 'Derived from DEX pair creation timestamp.',
    falsePositives: 'Tokens may have multiple pairs created at different times.',
    confidenceNote: 'Based on DEX listing data.',
  },
};

/* Risk level explanations */
export const RISK_LEVEL_EXPLAIN: Record<string, { label: string; meaning: string }> = {
  GREEN: {
    label: 'Low Risk Indicators',
    meaning: 'No significant risk indicators detected across analyzed data sources.',
  },
  YELLOW: {
    label: 'Moderate Risk Indicators',
    meaning: 'Some risk indicators present. Review the individual metric breakdowns for details.',
  },
  RED: {
    label: 'High Risk Indicators',
    meaning: 'Multiple significant risk indicators detected. Review all metric details carefully.',
  },
  UNKNOWN: {
    label: 'Insufficient Data',
    meaning: 'Insufficient intelligence data available to determine risk level.',
  },
};

/* Confidence level labels — accepts 0-100 scale */
export function getConfidenceLabel(confidence: number): { label: string; color: string } {
  if (confidence >= 70) return { label: 'High', color: 'var(--accent-success, #22c55e)' };
  if (confidence >= 40) return { label: 'Medium', color: 'var(--accent-warning, #eab308)' };
  return { label: 'Low', color: 'var(--accent-danger, #ef4444)' };
}

/** Legal disclaimer text — single source of truth */
export const LEGAL_DISCLAIMER =
  'CryptoGuardians provides blockchain intelligence for informational purposes only. Not financial advice.';
